// Вкладка «Приглашения»: создать ссылку (срок, число использований, роль, заметка) -> ссылка с «Копировать»;
// список с состоянием (действует, истекло, использовано, отозвано) и отзывом.
import { useState } from 'react';
import { ctx } from '../../core/ctx';
import { askConfirm } from '../../ui/prompts';
import { Btn, Field, Segmented } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { useAuth } from '../auth/session';
import { adminApi, type AInvite } from './adminApi';
import { act, copyText, CopyField, fmtDate, fmtLeft, LoadError, Pill, useLoad } from './common';

const TERMS: [string, string, number][] = [['1d', '1 день', 24], ['7d', '7 дней', 24 * 7], ['30d', '30 дней', 24 * 30], ['none', 'бессрочно', 0], ['own', 'своё', -1]];
const USES: [string, string, number][] = [['1', 'один человек', 1], ['30', 'до 30 (группа)', 30], ['0', 'без ограничения', 0], ['own', 'своё', -1]];

export const inviteUrl = (inv: AInvite) => location.origin + inv.path;

function state(inv: AInvite): { tone: 'ok' | 'warn' | 'err' | 'dim'; text: string } {
  if (inv.revoked) return { tone: 'dim', text: 'Отозвано' };
  if (inv.expires && inv.expires < Date.now() / 1000) return { tone: 'dim', text: 'Истекло' };
  if (inv.maxUses && inv.uses >= inv.maxUses) return { tone: 'dim', text: 'Использовано' };
  return { tone: 'ok', text: 'Действует' };
}

export function InvitesTab() {
  const reg = useAuth((s) => s.info?.registration);
  const { data, error, reload } = useLoad(() => adminApi.invites(), []);
  const [term, setTerm] = useState('7d');
  const [ownTerm, setOwnTerm] = useState('14');
  const [ownUnit, setOwnUnit] = useState<'24' | '1'>('24');
  const [uses, setUses] = useState('1');
  const [ownUses, setOwnUses] = useState('10');
  const [subDays, setSubDays] = useState('0');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [note, setNote] = useState('');
  const [created, setCreated] = useState<AInvite | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const preset = TERMS.find((t) => t[0] === term)![2];
      const hours = preset < 0 ? Number(ownTerm) * Number(ownUnit) : preset;
      if (!(hours >= 0)) throw new Error('Срок приглашения: укажите число');
      const inv = await adminApi.createInvite({ hours, maxUses: uses === 'own' ? Number(ownUses) : Number(uses), note: note.trim(), role, subDays: Number(subDays) || 0 });
      setCreated(inv);
      setNote('');
      await reload();
    } catch (e: any) { ctx.toast(e.message, 'error', 8000); } finally { setBusy(false); }
  };
  const list = (data?.invites || []).filter((i) => showAll || state(i).tone === 'ok');
  const inactive = (data?.invites || []).length - (data?.invites || []).filter((i) => state(i).tone === 'ok').length;
  return (
    <section className="adm-tab" data-testid="admin-invites">
      {reg === 'closed' && <div className="adm-warn"><Icon name="info" size={16} /><span>Регистрация сейчас закрыта (настройка «Регистрация») - по ссылкам
        всё равно можно зарегистрироваться, только если режим «По приглашениям» или «Открытая».</span>
        <Btn label="К настройкам" onClick={() => { location.hash = '#/admin/settings'; }} /></div>}
      <div className="adm-card adm-invite-new" data-testid="invite-form">
        <div className="adm-card-title"><Icon name="ticket" size={17} /> Новое приглашение</div>
        <div className="adm-invite-grid">
          <Field label="Действует"><Segmented value={term} onChange={setTerm} testid="invite-term" options={TERMS.map(([id, label]) => ({ id, label }))} />
            {term === 'own' && <span className="adm-row">
              <input type="number" min={1} value={ownTerm} onChange={(e) => setOwnTerm(e.target.value)} data-testid="invite-own-term" />
              <select value={ownUnit} onChange={(e) => setOwnUnit(e.target.value as '24' | '1')} data-testid="invite-own-unit"><option value="24">дней</option><option value="1">часов</option></select>
            </span>}
          </Field>
          <Field label="Сколько регистраций"><Segmented value={uses} onChange={setUses} testid="invite-uses" options={USES.map(([id, label]) => ({ id, label }))} />
            {uses === 'own' && <input type="number" min={1} value={ownUses} onChange={(e) => setOwnUses(e.target.value)} data-testid="invite-own-uses" />}
          </Field>
          <Field label="Подписка при регистрации, дней" hint="0 - без подписки: только просмотр отчётов"><input type="number" min={0} value={subDays} onChange={(e) => setSubDays(e.target.value)} data-testid="invite-sub-days" /></Field>
          <Field label="Роль"><Segmented value={role} onChange={setRole} testid="invite-role" options={[{ id: 'user', label: 'Пользователь' }, { id: 'admin', label: 'Администратор' }]} /></Field>
          <Field label="Заметка (для себя)"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="напр. группа 151115" maxLength={200} data-testid="invite-note" /></Field>
        </div>
        <div className="adm-row">
          <Btn icon="ticket" label="Создать ссылку-приглашение" primary onClick={create} disabled={busy} testid="invite-create" />
          {role === 'admin' && <span className="adm-warn-text"><Icon name="warn" size={14} /> по этой ссылке зарегистрируется администратор</span>}
        </div>
        {created && (
          <div className="adm-invite-result" data-testid="invite-result">
            <div className="adm-invite-result-head"><Icon name="ok" size={16} /> Ссылка готова - отправьте её тем, кого приглашаете
              {created.maxUses ? ` (регистраций: ${created.maxUses})` : ''}, действует {fmtLeft(created.expires)}{created.subDays ? `, подписка на ${created.subDays} дн.` : ''}.</div>
            <CopyField value={inviteUrl(created)} testid="invite-link" />
          </div>
        )}
      </div>

      <div className="adm-section-head">
        <span>Приглашения</span>
        <span className="topbar-spacer" />
        {inactive > 0 && <label className="check small"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="invites-all" /> показать недействующие ({inactive})</label>}
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="invites-table">
          <thead><tr><th>Заметка</th><th>Ссылка</th><th>Роль</th><th className="num">Регистрации</th><th>Срок</th><th>Состояние</th><th className="adm-col-act" /></tr></thead>
          <tbody>
            {list.map((inv) => {
              const st = state(inv);
              return (
                <tr key={inv.id} data-testid="invite-row" className={st.tone === 'dim' ? 'is-dim' : ''}>
                  <td>{inv.note || <span className="adm-dim">без заметки</span>}<div className="adm-sub">создано {fmtDate(inv.created)}{inv.subDays ? ` · подписка ${inv.subDays} дн.` : ''}</div></td>
                  <td><code className="adm-code" title={inviteUrl(inv)}>…?invite={inv.code.slice(0, 8)}…</code></td>
                  <td>{inv.role === 'admin' ? <Pill tone="warn">Администратор</Pill> : <span className="adm-dim">Пользователь</span>}</td>
                  <td className="num">{inv.uses}{inv.maxUses ? ` / ${inv.maxUses}` : ' / ∞'}</td>
                  <td title={inv.expires ? fmtDate(inv.expires, true) : ''}>{inv.expires ? fmtLeft(inv.expires) : 'бессрочно'}</td>
                  <td><Pill tone={st.tone}>{st.text}</Pill></td>
                  <td className="adm-col-act adm-actions">
                    {st.tone === 'ok' && <>
                      <Btn icon="copy" title="Скопировать ссылку" onClick={async () => {
                        ctx.toast(await copyText(inviteUrl(inv)) ? 'Ссылка скопирована' : 'Не удалось скопировать', 'ok');
                      }} testid="invite-copy-row" />
                      <Btn icon="ban" title="Отозвать" danger onClick={async () => {
                        if (await askConfirm({ title: 'Отозвать приглашение?', text: 'По этой ссылке больше нельзя будет зарегистрироваться. Уже созданные учётные записи останутся.', ok: 'Отозвать', danger: true })) {
                          void act(() => adminApi.revokeInvite(inv.id), 'Приглашение отозвано', reload);
                        }
                      }} testid="invite-revoke" />
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && !list.length && <div className="adm-empty"><Icon name="ticket" size={26} /><div>Действующих приглашений нет. Создайте ссылку выше и отправьте её.</div></div>}
      </div>
    </section>
  );
}
